import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { isEqual } from 'lodash';

import ValidationAlert from '../../../app/components/ValidationAlert';
import FormRow from '../../../app/components/FormRow';
import CurrencyControl from '../../../app/components/CurrencyControl';
import {
  CreateUpdateInvoicePaymentDto,
  GetInvoicePaymentDto,
  GetPaymentCollectionDtoOriginEnum,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { useNavigation } from '../../../common/navigation';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import momencik from '../../../common/momencik';
import {
  convertInvoicePayment,
  newInvoicePayment,
  validate,
} from '../../../common/spdCore/contexts/InvoicePaymentContext';
import { InvoicePaymentEditorTabs } from '../../containers/InvoicePaymentEditorView/InvoicePaymentEditorView';

interface InvoicePaymentBasicDataProps {
  invoicePayment?: GetInvoicePaymentDto;
  invoicePaymentId?: string;
  setSalesInvoiceId?: never; // not used
  setInvoicePaymentId: Dispatch<SetStateAction<string | undefined>>;
  setNotSavedTab: (tab: InvoicePaymentEditorTabs | undefined) => void;
  setActiveTab: (tab: string) => void;
  refreshInvoicePayment: () => Promise<void>;
}

export const InvoicePaymentBasicData: React.FC<
  InvoicePaymentBasicDataProps
> = props => {
  const [invoicePayment, setInvoicePayment] =
    useState<CreateUpdateInvoicePaymentDto>(newInvoicePayment());
  const [originalInvoicePayment, setOriginalInvoicePayment] =
    useState<CreateUpdateInvoicePaymentDto>(newInvoicePayment());
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const spd = useSpdCore();
  const nav = useNavigation();

  const isLinkedToReceipt =
    props.invoicePayment?.paymentCollection?.origin ===
    GetPaymentCollectionDtoOriginEnum.Receipt;

  const isExistingPayment = !!props.invoicePaymentId;

  useEffect(() => {
    if (props.invoicePayment) {
      const converted = convertInvoicePayment(props.invoicePayment);
      setInvoicePayment(converted);
      setOriginalInvoicePayment(converted);
    }
  }, [props.invoicePayment]);

  const propertyChange = (obj: Partial<CreateUpdateInvoicePaymentDto>) => {
    const updated = { ...invoicePayment, ...obj };
    const dataChanged = !isEqual(updated, originalInvoicePayment);
    props.setNotSavedTab(
      dataChanged ? InvoicePaymentEditorTabs.BASIC_DATA : undefined,
    );
    setInvoicePayment(updated);
  };

  const handleSave = async () => {
    setSaveErrors([]);
    setShowSaveErrors(false);
    setIsSaveLoading(true);

    try {
      const validationResult = await validate(invoicePayment);
      if (!validationResult.valid) {
        setSaveErrors(validationResult.errors);
        setShowSaveErrors(true);
        return;
      }

      const hasChanges = !isEqual(invoicePayment, originalInvoicePayment);

      if (hasChanges) {
        if (props.invoicePaymentId) {
          await spd.invoicePayments.update(
            props.invoicePaymentId,
            invoicePayment,
          );
        } else {
          const saved = await spd.invoicePayments.createSingle(invoicePayment);
          props.setInvoicePaymentId(saved.id);
          void nav.invoicePaymentEditor(saved.id);
        }
        setOriginalInvoicePayment(invoicePayment);
        props.setNotSavedTab(undefined);
        await props.refreshInvoicePayment();
      }

      props.setActiveTab(InvoicePaymentEditorTabs.LINKS);
    } catch (response) {
      const errors = await responseErrors(response as ResponseError);
      setSaveErrors(errors);
      setShowSaveErrors(true);
    } finally {
      setIsSaveLoading(false);
    }
  };

  return (
    <Form className="d-grid gap-4 mb-3">
      {isLinkedToReceipt && (
        <Alert variant="info">
          Ta wpłata została wygenerowana automatycznie na podstawie paragonu.
          Edycja jest niedostępna.
        </Alert>
      )}
      <FormRow controlId="sender" label="Nadawca">
        <Form.Control
          type="text"
          value={invoicePayment.sender || ''}
          onChange={e => propertyChange({ sender: e.target.value || null })}
          disabled={isLinkedToReceipt}
        />
      </FormRow>
      <FormRow controlId="title" label="Tytuł wpłaty">
        <Form.Control
          type="text"
          value={invoicePayment.title || ''}
          onChange={e => propertyChange({ title: e.target.value || null })}
          disabled={isLinkedToReceipt}
        />
      </FormRow>
      <FormRow controlId="amount" label="Kwota">
        <CurrencyControl
          value={invoicePayment.amount || ''}
          decimalScale={2}
          onChange={value => propertyChange({ amount: value || null })}
          disabled={isLinkedToReceipt || isExistingPayment}
        />
      </FormRow>
      <FormRow controlId="paymentDate" label="Data wpłaty">
        <Form.Control
          type="date"
          value={momencik(invoicePayment.paymentDate, 'YYYY-MM-DD')}
          onChange={e =>
            propertyChange({
              paymentDate: e.target.value ? new Date(e.target.value) : null,
            })
          }
          disabled={isLinkedToReceipt || isExistingPayment}
        />
      </FormRow>
      <ValidationAlert
        show={showSaveErrors}
        errors={saveErrors}
        className="mt-3"
      />
      <div className="ms-auto d-flex gap-2">
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isSaveLoading || isLinkedToReceipt}
        >
          {isSaveLoading ? 'Zapisywanie...' : 'Zapisz i przejdź dalej'}
        </Button>
      </div>
    </Form>
  );
};
